import React, { useMemo } from "react"
import { Helmet } from "react-helmet"
import { normalizeJsonLd } from "../utils/schema"
import { isInDevelopment, IN_DEVELOPMENT_ROBOTS } from "../data/inDevelopment"

const DEFAULT_IMAGE = "https://abox.agency/assets/image/meta_og_image.jpg"
const DEFAULT_SITE_NAME = "Abox Agency"
const DEFAULT_TWITTER_HANDLE = "@aBox_Agency"

export const SEO = ({
    title,
    description,
    keywords,
    url,
    image = DEFAULT_IMAGE,
    type = "website",
    siteName = DEFAULT_SITE_NAME,
    ogTitle,
    ogDescription,
    twitterTitle,
    twitterDescription,
    robots = "index, follow",
    author = "Abox Agency",
    schema,
    meta = [],
}) => {
    // A route listed in src/data/inDevelopment.js is forced to noindex, and the
    // page cannot override it with its own `robots` prop. That is deliberate:
    // launching a page then forgetting to un-noindex it would silently keep it
    // out of search, so the removal has to live in exactly one place.
    const resolvedRobots = isInDevelopment(url) ? IN_DEVELOPMENT_ROBOTS : robots

    const resolvedOgTitle = ogTitle || title
    const resolvedOgDescription = ogDescription || description
    const resolvedTwitterTitle = twitterTitle || resolvedOgTitle
    const resolvedTwitterDescription = twitterDescription || resolvedOgDescription
    const jsonLd = useMemo(() => {
        if (!schema) return null
        try {
            return JSON.stringify(normalizeJsonLd(schema))
        } catch {
            return null
        }
    }, [schema])

    return (
        <Helmet meta={meta}>
            <title>{title}</title>
            <meta name="title" content={title} />
            <meta name="description" content={description} />
            {keywords ? <meta name="keywords" content={keywords} /> : null}
            <meta name="robots" content={resolvedRobots} />
            <meta name="author" content={author} />

            <link rel="canonical" href={url} />

            <meta property="og:type" content={type} />
            <meta property="og:url" content={url} />
            <meta property="og:title" content={resolvedOgTitle} />
            <meta property="og:description" content={resolvedOgDescription} />
            <meta property="og:image" content={image} />
            <meta property="og:site_name" content={siteName} />
            <meta property="og:locale" content="en_US" />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={url} />
            <meta name="twitter:title" content={resolvedTwitterTitle} />
            <meta name="twitter:description" content={resolvedTwitterDescription} />
            <meta name="twitter:image" content={image} />
            <meta name="twitter:site" content={DEFAULT_TWITTER_HANDLE} />

            {jsonLd ? (
                <script type="application/ld+json">{jsonLd}</script>
            ) : null}
        </Helmet>
    )
}